import { useId } from 'react'
import { useHitTarget } from '../responsive/hitTarget'
import { CategoryIcon } from '../icons/CategoryIcon'
import { BY_SLUG } from '../icons/categoryIconPaths'
import './iconGrid.css'

/**
 * Pick one glyph from a supplied set — Task 14. A radiogroup over a supplied
 * set, with a `CategoryIcon` in each cell. (It was built as the sibling of a
 * colour grid with the same shape; #454 deleted that grid with category
 * colour, and `CatalogueIconGrid` is now the shape's other user.)
 *
 * **`icons` is an array of design SLUGS** (`groceries`, `coffee-shops`) —
 * the vocabulary `CategoryIcon.tsx`'s fallback chain still reads back for a
 * record saved before the catalogue existed. Each slug is resolved to a glyph
 * through `BY_SLUG` — the same map `CategoryIcon` uses — so the two places
 * that know a slug names a glyph stay the one place.
 *
 * **No form writes these slugs any more.** The category form stopped at #447
 * and the merchant form at #461: both write paths 422 anything outside the
 * backend catalogue, so both pick through `CatalogueIconGrid` instead. What
 * is left for this grid is the design-system gallery (`registry.tsx`) and any
 * future picker over a set the server does not validate. It stays because the
 * bundled pack is still the DISPLAY fallback; it is no longer an input to a
 * server-validated field.
 *
 * **The set is static** — `META` is a source import, not a runtime
 * derivation, so a caller passes `Object.values(META)` (or a subset of it)
 * directly rather than through a hook.
 *
 * Three fixes the deleted colour grid needed on review, inherited rather than
 * rediscovered:
 *
 * **`useId`, not `label`, for the radio `name`.** `name` scopes native
 * radio-group exclusivity, and a caller's `label` text is not unique —
 * `StatesReference.tsx` renders every registry entry with no `<form>`
 * boundary between instances, so a shared `name` would let selecting a glyph
 * in one instance natively uncheck an unrelated instance elsewhere on the
 * page. Called unconditionally, before the empty-set early return — a hook
 * placed after it breaks the rules of hooks on the real path where the set
 * legitimately starts empty (a category with `is_system` icons still
 * loading, say), even though every test here supplies a non-empty set.
 *
 * **`useHitTarget`, on the cell, for both axes.** `e2e/hitTargets.mjs`'s
 * `CONTROL_SELECTOR` measures the `<input>` itself, so the real, tappable box
 * has to clear M0-R29's floor — the glyph stays visually 20px, the cell is
 * floored via inline `minHeight`/`minWidth`, and the input is stretched to
 * fill the enlarged cell (`iconGrid.css`) rather than clipped to 1px.
 *
 * **`aria-label` is the glyph's human name**, resolved through `BY_SLUG` —
 * `'coffee-shops'` announces as "Coffee Shops". It falls back to the
 * positional `${label} ${index + 1} of ${icons.length}` only for a slug the
 * map does not know.
 *
 * This is where `IconGrid` deliberately diverged from the colour grid it was
 * modelled on, and the divergence is the point. A positional label was right
 * for a colour swatch: a hex value is not a name a person can use, so
 * position was the only honest thing left to say. This component copied that
 * reasoning across, and final
 * review caught that it does not transfer — the argument "a slug tells a
 * screen-reader user as little as a hex string does" is true of the slug and
 * false of what the slug resolves to, which this file already looks up one
 * line below to pick the glyph. As shipped, a screen-reader user choosing a
 * category icon was choosing an ordinal out of 88.
 *
 * Selection is exposed via `aria-checked`, never by appearance alone
 * (M0-R11) — the checked cell also carries a focus-style ring
 * (`iconGrid.css`), the same mechanism `CatalogueIconGrid` uses, so the visual
 * signal is never colour or fill alone either.
 */
export function IconGrid({
  icons,
  value,
  onChange,
  label,
  disabled,
}: {
  icons: string[]
  value: string | null
  onChange: (icon: string) => void
  label: string
  disabled?: boolean
}) {
  // Called before the early return below: hooks run every render regardless
  // of `icons.length`.
  const groupId = useId()
  // `'page'`: the same default `fineFloorFor` in `e2e/hitTargets.mjs` falls
  // back to for a control that claims no size class of its own.
  const target = useHitTarget('page')

  if (icons.length === 0) return null

  return (
    <div className="icon-grid" role="radiogroup" aria-label={label}>
      {icons.map((icon, index) => (
        // `key={icon}` assumes distinct slugs. Holds today because `META`'s
        // slugs are derived one-per-category-name and every caller passes
        // either the full set or a hand-picked, non-repeating subset of it —
        // nothing here enforces it.
        <label
          key={icon}
          className="icon-grid__cell"
          style={{ minHeight: target, minWidth: target }}
        >
          <input
            type="radio"
            name={groupId}
            className="icon-grid__input"
            checked={value === icon}
            onChange={() => onChange(icon)}
            disabled={disabled}
            aria-label={BY_SLUG[icon] ?? `${label} ${index + 1} of ${icons.length}`}
          />
          <span className="icon-grid__glyph">
            <CategoryIcon category={{ name: BY_SLUG[icon] ?? 'Uncategorized' }} size={20} />
          </span>
        </label>
      ))}
    </div>
  )
}
